/**
 * Admin Logs API Endpoints
 * API service for viewing server log files (Admin only)
 */

import { ApiService } from '../apiService'
import { resolveApiUrl } from '../client'
import { tokenStorage } from '@services/tokenStorage'
import type {
  LogFilesResponse,
  LogFileContentParams,
  LogFileContentResponse,
  LogFileTailParams,
  LogFileTailResponse,
} from '../types/logs'

export const logsApi = {
  // ===================
  // LOG FILES
  // ===================

  /** Get list of all available log files with size and dates */
  getLogFiles: () =>
    ApiService.get<LogFilesResponse>('/admin/logs/files'),

  /** Get paginated content of a log file (oldest to newest) */
  getLogFileContent: (fileName: string, params?: LogFileContentParams) =>
    ApiService.get<LogFileContentResponse>(`/admin/logs/files/${encodeURIComponent(fileName)}`, params),

  /** Get most recent N lines of a log file (for live monitoring) */
  getLogFileTail: (fileName: string, params?: LogFileTailParams) =>
    ApiService.get<LogFileTailResponse>(`/admin/logs/files/${encodeURIComponent(fileName)}/tail`, params),

  /** Download entire log file */
  downloadLogFile: async (fileName: string) => {
    const token = tokenStorage.getAccessToken()
    const response = await fetch(resolveApiUrl(`/admin/logs/files/${encodeURIComponent(fileName)}/download`), {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })

    if (!response.ok) {
      throw new Error('Failed to download log file')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
  },
}
